import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { taxReports } from '@/api/taxReports';
import { orgs } from '@/api/catalogs';
import { pickDefaultOrg } from '@/store/defaults';

type ReportType = '4DF' | 'ESV' | 'VAT_D1';

const fmt = (v: number) => v.toLocaleString('uk-UA', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export default function TaxReportsPage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const [reportType, setReportType] = useState<ReportType>('4DF');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);
  }, [orgList.data, orgId]);

  const data4dfQ = useQuery({
    queryKey: ['4df', orgId, year, quarter],
    queryFn: () => taxReports.get4df(orgId, year, quarter),
    enabled: !!orgId && reportType === '4DF',
  });
  const esvQ = useQuery({
    queryKey: ['esv', orgId, year, month],
    queryFn: () => taxReports.getEsv(orgId, year, month),
    enabled: !!orgId && reportType === 'ESV',
  });
  const d1Q = useQuery({
    queryKey: ['vat-d1', orgId, year, month],
    queryFn: () => taxReports.getVatD1(orgId, year, month),
    enabled: !!orgId && reportType === 'VAT_D1',
  });

  async function downloadXml() {
    let blob: Blob;
    let filename: string;
    if (reportType === '4DF') {
      blob = await taxReports.download4dfXml(orgId, year, quarter);
      filename = `4DF_${year}_Q${quarter}.xml`;
    } else if (reportType === 'ESV') {
      blob = await taxReports.downloadEsvXml(orgId, year, month);
      filename = `ESV_${year}_${String(month).padStart(2, '0')}.xml`;
    } else {
      blob = await taxReports.downloadVatD1Xml(orgId, year, month);
      filename = `VAT_D1_${year}_${String(month).padStart(2, '0')}.xml`;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Регламентована звітність ДПС</h2>
        <button className="primary" onClick={downloadXml} disabled={!orgId}>
          ⬇ Завантажити XML
        </button>
      </div>

      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        ⚠ XML-файли — <strong>чорновики</strong>. Перед поданням потребують
        перевірки актуальною XSD-схемою ДПС і підпису КЕП. Подавати через
        Кабінет платника податків або «Єдине вікно».
      </p>

      <div className="doc-grid doc-grid--5" style={{ marginBottom: '1rem' }}>
        <div className="field">
          <label>Звіт</label>
          <select value={reportType} onChange={(e) => setReportType(e.target.value as ReportType)}>
            <option value="4DF">4ДФ (квартал)</option>
            <option value="ESV">ЄСВ (місяць)</option>
            <option value="VAT_D1">Д1 до ПДВ (місяць)</option>
          </select>
        </div>
        <div className="field">
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) =>
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>,
            )}
          </select>
        </div>
        <div className="field">
          <label>Рік</label>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </div>
        {reportType === '4DF' ? (
          <div className="field">
            <label>Квартал</label>
            <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
              <option value={1}>I</option>
              <option value={2}>II</option>
              <option value={3}>III</option>
              <option value={4}>IV</option>
            </select>
          </div>
        ) : (
          <div className="field">
            <label>Місяць</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {['Січень','Лютий','Березень','Квітень','Травень','Червень',
                'Липень','Серпень','Вересень','Жовтень','Листопад','Грудень']
                .map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
            </select>
          </div>
        )}
      </div>

      {reportType === '4DF' && data4dfQ.data && (
        <Render4DF data={data4dfQ.data} />
      )}
      {reportType === 'ESV' && esvQ.data && (
        <RenderESV data={esvQ.data} />
      )}
      {reportType === 'VAT_D1' && d1Q.data && (
        <RenderVATD1 data={d1Q.data} />
      )}
    </div>
  );
}

function Render4DF({ data }: { data: any }) {
  return (
    <>
      <h3>4ДФ — {data.organization.full_name} (Q{data.period.quarter} {data.period.year})</h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        Підсумок за квартал: брутто {fmt(data.totals.gross)},
        ПДФО {fmt(data.totals.pdfo)},
        ВЗ {fmt(data.totals.wz)},
        співробітників: {data.totals.employees_count}
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ background: 'var(--bg-soft-2)' }}>
            <th style={{ padding: '0.4rem', textAlign: 'left', width: 30 }}>№</th>
            <th style={{ padding: '0.4rem', textAlign: 'left' }}>ПІБ</th>
            <th style={{ padding: '0.4rem', textAlign: 'left', width: 110 }}>ІПН</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>Брутто</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>ПДФО</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>ВЗ</th>
            <th style={{ padding: '0.4rem', textAlign: 'center', width: 60 }}>Ознака</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((it: any, idx: number) => (
            <tr key={it.individual_id} style={{ borderTop: '1px solid var(--border-soft)' }}>
              <td style={{ padding: '0.4rem' }}>{idx + 1}</td>
              <td style={{ padding: '0.4rem' }}>{it.full_name}</td>
              <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{it.inn}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.gross_accrued)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.pdfo_accrued)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.wz_accrued)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'center' }}>{it.income_sign}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function RenderESV({ data }: { data: any }) {
  return (
    <>
      <h3>ЄСВ-звіт — {data.organization.full_name} ({data.period.month}.{data.period.year})</h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        База: {fmt(data.totals.gross)},
        ЄСВ: {fmt(data.totals.esv)},
        співробітників: {data.totals.employees_count}
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ background: 'var(--bg-soft-2)' }}>
            <th style={{ padding: '0.4rem', textAlign: 'left', width: 30 }}>№</th>
            <th style={{ padding: '0.4rem', textAlign: 'left' }}>ПІБ</th>
            <th style={{ padding: '0.4rem', textAlign: 'left', width: 110 }}>ІПН</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>Брутто</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>База ЄСВ</th>
            <th style={{ padding: '0.4rem', textAlign: 'right', width: 70 }}>Ставка, %</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>Сума ЄСВ</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((it: any, idx: number) => (
            <tr key={it.individual_id} style={{ borderTop: '1px solid var(--border-soft)' }}>
              <td style={{ padding: '0.4rem' }}>{idx + 1}</td>
              <td style={{ padding: '0.4rem' }}>{it.full_name}</td>
              <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{it.inn}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.gross)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.esv_base)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{it.esv_rate}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.esv)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function RenderVATD1({ data }: { data: any }) {
  const totalO = data.obligations.reduce((s: number, x: any) => s + x.vat, 0);
  const totalC = data.credit.reduce((s: number, x: any) => s + x.vat, 0);
  return (
    <>
      <h3>Д1 до Декларації з ПДВ — {data.organization.full_name} ({data.period.month}.{data.period.year})</h3>
      <h4>Розділ I. Податкові зобов'язання (продажі)</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', marginBottom: '1rem' }}>
        <thead>
          <tr style={{ background: 'var(--bg-soft-2)' }}>
            <th style={{ padding: '0.4rem', textAlign: 'left', width: 30 }}>№</th>
            <th style={{ padding: '0.4rem', textAlign: 'left', width: 110 }}>ЄДРПОУ/ІПН</th>
            <th style={{ padding: '0.4rem', textAlign: 'left' }}>Контрагент</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>Без ПДВ</th>
            <th style={{ padding: '0.4rem', textAlign: 'right', width: 60 }}>Ставка</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>ПДВ</th>
          </tr>
        </thead>
        <tbody>
          {data.obligations.map((it: any, idx: number) => (
            <tr key={idx} style={{ borderTop: '1px solid var(--border-soft)' }}>
              <td style={{ padding: '0.4rem' }}>{idx + 1}</td>
              <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{it.edrpou}</td>
              <td style={{ padding: '0.4rem' }}>{it.name}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.amount)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{it.vat_rate}%</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.vat)}</td>
            </tr>
          ))}
          <tr style={{ background: 'var(--primary-soft)', fontWeight: 700 }}>
            <td colSpan={5} style={{ padding: '0.4rem', textAlign: 'right' }}>Усього ПДВ зобов'язань:</td>
            <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(totalO)}</td>
          </tr>
        </tbody>
      </table>

      <h4>Розділ II. Податковий кредит (закупки)</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ background: 'var(--bg-soft-2)' }}>
            <th style={{ padding: '0.4rem', textAlign: 'left', width: 30 }}>№</th>
            <th style={{ padding: '0.4rem', textAlign: 'left', width: 110 }}>ЄДРПОУ/ІПН</th>
            <th style={{ padding: '0.4rem', textAlign: 'left' }}>Контрагент</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>Без ПДВ</th>
            <th style={{ padding: '0.4rem', textAlign: 'right', width: 60 }}>Ставка</th>
            <th style={{ padding: '0.4rem', textAlign: 'right' }}>ПДВ</th>
          </tr>
        </thead>
        <tbody>
          {data.credit.map((it: any, idx: number) => (
            <tr key={idx} style={{ borderTop: '1px solid var(--border-soft)' }}>
              <td style={{ padding: '0.4rem' }}>{idx + 1}</td>
              <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{it.edrpou}</td>
              <td style={{ padding: '0.4rem' }}>{it.name}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.amount)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{it.vat_rate}%</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(it.vat)}</td>
            </tr>
          ))}
          <tr style={{ background: 'var(--primary-soft)', fontWeight: 700 }}>
            <td colSpan={5} style={{ padding: '0.4rem', textAlign: 'right' }}>Усього ПДВ кредиту:</td>
            <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmt(totalC)}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
}
